import { Button, DynamicContainer, Form, FormContext, FormSection } from '@hai/ui-react';
import { FormikProps } from 'formik';
import { isNil } from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormSelect, SelectOptions } from 'src/js/component/base/form/form-select';
import { FormValue } from 'src/js/component/base/form/form-value';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { IsSavedRef, RebootIsSaved } from 'src/js/reboot/reboot-is-saved';
import { RebootNeeded, RebootNeededRef } from 'src/js/reboot/reboot-needed';
import { SettingsTabContent } from 'src/js/pages/settings/settings-tab-content';
import { validateFormContext } from 'src/js/validator/validator';

import { fetchComPort, updateComPort } from './comport-store';
import {
  ComPortMode,
  ComPortViewModel,
  baudRateOptions,
  getFlowControlString,
  getParityString,
  getStandardString,
  getStopBitsString,
} from './comport-view-model';

export const ComPort: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const formRef = useRef();

  const getModeOptions = () => {
    const options: SelectOptions = [{ value: ComPortMode.MANAGEMENT, label: t('settings.comport.mode.management') }];
    if (systemInfoCapabilities.hasComPort() && systemInfoCapabilities.hasMetadata()) {
      options.push({ value: ComPortMode.METDATA, label: t('settings.comport.mode.metadata') });
    }
    if (systemInfoCapabilities.hasComPort() && !systemInfoCapabilities.isDVI()) {
      options.push({
        value: ComPortMode.PASSTHROUGH,
        label: t('settings.comport.mode.passThrough'),
      });
    }

    return options;
  };

  const [comPort, setComPort] = useState<ComPortViewModel>(undefined);
  useEffect(() => {
    fetchComPort().then((res: any) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          setComPort(response);
        }
      }
    });
  }, []);

  const rebootNeededRef = useRef<RebootNeededRef>(null);

  // the reboot handling for the reboot button
  const childRef = useRef<IsSavedRef>(null);
  const handleReboot = (): void => {
    childRef.current.show();
  };

  const [buttonState, setButtonState] = useState(undefined);
  const handleSubmit = (values: any) => {
    setButtonState('pending');
    updateComPort({ ...values, mode: Number(values.mode) })
      .then((res: any) => {
        notificationHandler(
          res,
          t('settings.comport.notifications.changeSuccessMsg'),
          t('settings.comport.notifications.changeErrorMsg'),
          t,
        );
        return res;
      })
      .then((res: any) => {
        if (res.ok) {
          if (comPort.mode !== res.data.mode) {
            rebootNeededRef.current.show();
          }
          setComPort(res.data);
        }
        setButtonState(undefined);
        return res;
      });
  };

  if (isNil(comPort)) {
    return null;
  }

  return (
    <SettingsTabContent name={t('settings.comport.title')}>
      <Form
        initialValues={comPort}
        defaultValidation={true}
        handleSubmit={handleSubmit}
        restValidationProps={{
          enableReinitialize: true,
          innerRef: formRef,
        }}
      >
        <FormContext.Consumer>
          {(formContext: FormikProps<ComPortViewModel>) => {
            const values = formContext.values;
            return (
              <>
                <div className="buttonRow">
                  <Button variant="secondary" onClick={handleReboot}>
                    {t('general.reboot')}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => validateFormContext(formContext, t)}
                    disabled={!formContext.dirty}
                    state={buttonState}
                  >
                    {t('general.apply')}
                  </Button>
                </div>
                <FormSection title={t('settings.comport.general')}>
                  <DynamicContainer minColumns={3}>
                    <FormSelect
                      name="mode"
                      title={t('settings.comport.form.mode')}
                      defaultValue={comPort.mode}
                      options={getModeOptions()}
                    />
                  </DynamicContainer>
                  <DynamicContainer>
                    <FormValue
                      label={t('settings.comport.form.standard')}
                      value={getStandardString(values.standard, t)}
                      className="hai-mb-6"
                    />
                    <FormSelect
                      name="baudRate"
                      title={t('settings.comport.form.baudRate')}
                      defaultValue={values.baudRate}
                      options={baudRateOptions}
                      disabled={values.mode !== ComPortMode.PASSTHROUGH || !values.passthroughActive}
                    />
                    <FormValue
                      label={t('settings.comport.form.dataBits')}
                      value={values.dataBits.toString()}
                      className="hai-mb-6"
                    />
                    <FormValue
                      label={t('settings.comport.form.parity')}
                      value={getParityString(values.parity, t)}
                      className="hai-mb-6"
                    />
                    <FormValue
                      label={t('settings.comport.form.stopBits')}
                      value={getStopBitsString(values.stopBits, t)}
                    />
                    <FormValue
                      label={t('settings.comport.form.flowControl')}
                      value={getFlowControlString(values.flowControl, t)}
                    />
                  </DynamicContainer>
                </FormSection>
              </>
            );
          }}
        </FormContext.Consumer>
      </Form>
      <RebootIsSaved ref={childRef} />
      <RebootNeeded ref={rebootNeededRef} />
    </SettingsTabContent>
  );
};
